import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/format-bytes';
import type {
  ResourceMetricRangeStat,
  ResourceMetricsRangeDistributionMetric,
  ResourceMetricsRangeWindow,
} from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const WINDOW_OPTIONS: { value: ResourceMetricsRangeWindow; label: string }[] = [
  { value: '1h', label: '1시간' },
  { value: '6h', label: '6시간' },
  { value: '24h', label: '24시간' },
  { value: '7d', label: '7일' },
  { value: '30d', label: '30일' },
];

const AXIS_COLOR = 'hsl(var(--foreground-muted))';
const GRID_COLOR = 'hsl(var(--border-muted))';
const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--surface))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 6,
  fontSize: 12,
} as const;
// 차트 안에서 Legend 가 tooltip 보다 위에 떠 보이는 현상 방지 — wrapper 의 z-index 를 키움.
const TOOLTIP_WRAPPER_STYLE = { zIndex: 50 } as const;

// 색상 팔레트 — 시리즈가 여러 개일 때 (GPU 0~N).
const SERIES_COLORS = [
  '#f97316', // orange-500
  '#14b8a6', // teal-500
  '#06b6d4', // cyan-500
  '#a855f7', // purple-500
  '#facc15', // yellow-400
  '#ec4899', // pink-500
  '#22c55e', // green-500
  '#3b82f6', // blue-500
];

function colorOf(i: number): string {
  return SERIES_COLORS[i % SERIES_COLORS.length] ?? '#f97316';
}

/**
 * 기간별 쿼리 공통 옵션. 분 단위 적재라 60초마다 자동 갱신이면 충분.
 * 탭 비활성 시엔 폴링 중단 (`refetchIntervalInBackground: false`).
 */
const RANGE_QUERY_OPTIONS = {
  refetchInterval: 60_000,
  refetchIntervalInBackground: false,
} as const;

function formatNum(value: number | null, fractionDigits = 1, suffix = ''): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(fractionDigits)}${suffix}`;
}

/** 1234 → "1.2K", 43200 → "43K", 1_200_000 → "1.2M". 30일 적재 (~43K row) 까지 안전. */
function formatCountCompact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

// ───────────────────────────────────────────────────────────────
// Top-level
// ───────────────────────────────────────────────────────────────

export function ResourceRangePanel({ hostId }: { hostId: string }) {
  const [window, setWindow] = useState<ResourceMetricsRangeWindow>('1h');

  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold">기간별 리소스 현황</h2>
          <p className="text-xs text-foreground-muted mt-0.5">
            1분 단위 집계 데이터 기반. 평균/최대/p95/최소 통계, 시계열 추이, 사용률 분포, 시간대 ×
            요일 히트맵.
          </p>
        </div>
        <WindowToggle value={window} onChange={setWindow} />
      </div>

      <TimeseriesBlock hostId={hostId} window={window} />
      <StatisticsBlock hostId={hostId} window={window} />
      <DistributionBlock hostId={hostId} window={window} />
      <HeatmapBlock hostId={hostId} window={window} />
    </section>
  );
}

function WindowToggle({
  value,
  onChange,
}: {
  value: ResourceMetricsRangeWindow;
  onChange: (v: ResourceMetricsRangeWindow) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-surface p-0.5">
      {WINDOW_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            'px-3 h-7 text-xs rounded transition-colors',
            value === opt.value
              ? 'bg-accent text-accent-foreground'
              : 'text-foreground-muted hover:text-foreground',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// 1. 통계 카드 블록
// ───────────────────────────────────────────────────────────────

function StatisticsBlock({
  hostId,
  window,
}: {
  hostId: string;
  window: ResourceMetricsRangeWindow;
}) {
  const query = useQuery({
    queryKey: ['admin', 'resource-metrics-range-statistics', hostId, window],
    queryFn: () => api.adminGetResourceMetricsRangeStatistics(hostId, window),
    ...RANGE_QUERY_OPTIONS,
  });

  if (query.isLoading) {
    return <p className="text-sm text-foreground-muted">통계 불러오는 중…</p>;
  }
  if (query.error) {
    return (
      <p className="text-sm text-destructive">
        통계를 불러올 수 없습니다:{' '}
        {query.error instanceof ApiError ? query.error.message : '알 수 없는 오류'}
      </p>
    );
  }
  if (!query.data) return null;
  const s = query.data;
  if (s.bucketCount === 0) {
    return (
      <div className="border border-dashed border-border rounded-2xl p-8 text-center text-sm text-foreground-muted">
        이 기간에 적재된 데이터가 없습니다. 호스트 등록 직후라면 1분~수분 기다리세요.
      </div>
    );
  }

  // 카드 두 영역:
  //   1) GPU 통합 카드 — 한 GPU 당 4 메트릭 × 4 통계 를 한 카드에. 데스크탑 한 행에 2장씩.
  //      (시스템 카드와 같은 4 컬럼 grid 에 넣으면 내부 sub-column 4개 때문에 너무 좁아짐)
  //   2) 시스템 4 카드 — 기존 그대로 (CPU%/CPU°C/Mem/Disk), grid-cols-4.
  return (
    <div className="space-y-3">
      {s.gpus.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {s.gpus.map((g) => (
            <GpuStatCard key={`gpu-${g.index}`} gpu={g} />
          ))}
        </div>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <SystemStatCard
          title="CPU 사용률"
          stat={s.cpu.utilizationPct}
          suffix=" %"
          footnote={s.cpu.cores > 0 ? `${s.cpu.cores} Cores` : undefined}
        />
        <SystemStatCard
          title="CPU 온도"
          stat={s.cpu.temperatureC}
          suffix=" °C"
          footnote={s.cpu.cores > 0 ? `${s.cpu.cores} Cores` : undefined}
        />
        <SystemStatCard
          title="Memory 사용률"
          stat={s.memory}
          suffix=" %"
          footnote={s.memoryTotalBytes > 0 ? `총 ${formatBytes(s.memoryTotalBytes)}` : undefined}
        />
        <SystemStatCard
          title="Disk 사용률"
          stat={s.disk}
          suffix=" %"
          footnote={s.diskTotalBytes > 0 ? `총 ${formatBytes(s.diskTotalBytes)}` : undefined}
        />
      </div>
    </div>
  );
}

/**
 * GPU 1 장 통합 카드 — 4 메트릭 (VRAM / 사용률 / 전력량 / 온도) 을 가로 4 컬럼.
 *
 * 각 컬럼: 라벨 / 평균(큰 숫자) / 최대·p95·최소 (작은 글씨). 평균이 "이 윈도우의 대표값"
 * 으로 즉시 인지되도록 시각적 위계를 잡고, 다른 통계는 secondary 로 같은 컬럼 아래.
 */
function GpuStatCard({
  gpu,
}: {
  gpu: import('@innermate/shared').ResourceMetricsRangeGpuStat;
}) {
  const columns: { label: string; stat: ResourceMetricRangeStat; suffix: string }[] = [
    { label: 'VRAM', stat: gpu.memoryPct, suffix: ' %' },
    { label: '사용률', stat: gpu.utilizationPct, suffix: ' %' },
    { label: '전력량', stat: gpu.powerW, suffix: ' W' },
    { label: '온도', stat: gpu.temperatureC, suffix: ' °C' },
  ];
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-3">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">GPU {gpu.index} 통계</h3>
          <p className="text-[10px] text-foreground-muted mt-0.5 truncate">{gpu.name}</p>
        </div>
        {gpu.memoryTotalBytes > 0 ? (
          <span className="text-[10px] text-foreground-muted shrink-0 whitespace-nowrap">
            총 VRAM {formatBytes(gpu.memoryTotalBytes)}
          </span>
        ) : null}
      </header>
      {/*
        column 사이에 vertical separator — grid 의 인접 자식에 자동으로 border-left 적용
        (divide-x). 외곽 column 도 균일하게 px-3 패딩을 줘 가로폭이 일정.
       */}
      <div className="grid grid-cols-4 divide-x divide-border">
        {columns.map((c) => (
          <div key={c.label} className="flex flex-col gap-2 min-w-0 px-3 first:pl-0 last:pr-0">
            {/*
              컬럼 헤더 — 통계 라벨 ("평균"/"최대"/...) 보다 한 단계 진하게 + bottom border 로
              "이 컬럼이 어떤 메트릭인가" 가 즉시 인지되도록 헤더 영역을 분리.
             */}
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider pb-1.5 border-b border-border">
              {c.label}
            </p>
            <dl className="tabular-nums space-y-1">
              <div className="flex justify-between items-baseline gap-1">
                <dt className="text-[10px] uppercase tracking-wider text-foreground-muted">평균</dt>
                <dd className="text-base font-semibold whitespace-nowrap">
                  {formatNum(c.stat.avg, 1, c.suffix)}
                </dd>
              </div>
              <div className="flex justify-between gap-1 text-[10px] text-foreground-muted">
                <dt>최대</dt>
                <dd className="whitespace-nowrap">{formatNum(c.stat.max, 1, c.suffix)}</dd>
              </div>
              <div className="flex justify-between gap-1 text-[10px] text-foreground-muted">
                <dt>p95</dt>
                <dd className="whitespace-nowrap">{formatNum(c.stat.p95, 1, c.suffix)}</dd>
              </div>
              <div className="flex justify-between gap-1 text-[10px] text-foreground-muted">
                <dt>최소</dt>
                <dd className="whitespace-nowrap">{formatNum(c.stat.min, 1, c.suffix)}</dd>
              </div>
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

// CPU/Memory/Disk — 단일 메트릭 카드, 통계 4개를 가로 한 줄로 배치 (라벨/값 2 row × 4 col).
function SystemStatCard({
  title,
  stat,
  suffix,
  footnote,
}: {
  title: string;
  stat: ResourceMetricRangeStat;
  suffix: string;
  footnote?: string;
}) {
  const cells: { label: string; value: number | null; emphasis?: boolean }[] = [
    { label: '평균', value: stat.avg, emphasis: true },
    { label: '최대', value: stat.max },
    { label: 'p95', value: stat.p95 },
    { label: '최소', value: stat.min },
  ];
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-2">
      <header className="flex items-start justify-between gap-2">
        <h3 className="text-xs font-medium text-foreground-muted">{title}</h3>
        {footnote ? (
          <span className="text-[10px] text-foreground-muted shrink-0 whitespace-nowrap">
            {footnote}
          </span>
        ) : null}
      </header>
      <div className="grid grid-cols-4 gap-x-2 text-[11px]">
        {cells.map((c) => (
          <div key={c.label} className="text-foreground-muted">
            {c.label}
          </div>
        ))}
        {cells.map((c) => (
          <div
            key={`v-${c.label}`}
            className={cn('tabular-nums', c.emphasis ? 'font-semibold' : undefined)}
          >
            {formatNum(c.value, 1, suffix)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// 2. 시계열 라인 차트
// ───────────────────────────────────────────────────────────────

// Y축 도메인 — 데이터 범위에 딱 맞추되 약간 padding 으로 라인이 차트 가장자리에 붙는 걸 방지.
// `'dataMin' / 'dataMax'` 문자열은 recharts 가 인식. 사용자 요청대로 정적 0~100 대신 dynamic 범위.
const yDomainPct: [(min: number) => number, (max: number) => number] = [
  (min: number) => Math.max(0, Math.floor(min - 2)),
  (max: number) => Math.min(100, Math.ceil(max + 2)),
];
const yDomainAuto: [(min: number) => number, (max: number) => number] = [
  (min: number) => Math.floor(min - 2),
  (max: number) => Math.ceil(max + 2),
];

function TimeseriesBlock({
  hostId,
  window,
}: {
  hostId: string;
  window: ResourceMetricsRangeWindow;
}) {
  const query = useQuery({
    queryKey: ['admin', 'resource-metrics-range-timeseries', hostId, window],
    queryFn: () => api.adminGetResourceMetricsRangeTimeseries(hostId, window),
    ...RANGE_QUERY_OPTIONS,
  });

  if (query.isLoading) return null;
  if (query.error || !query.data) return null;
  const { points, gpus } = query.data;
  if (points.length === 0) return null;

  // 시계열을 recharts dataKey 별로 풀어줌. points 자체는 nested 배열 (gpuUtilPcts) 라
  // chart 친화 row 형태로 transform.
  const rows = points.map((p) => {
    const row: Record<string, number | string | null> = {
      bucketAt: p.bucketAt,
      cpuUtil: p.cpuUtilPct,
      cpuTemp: p.cpuTempC,
      memory: p.memoryPct,
      disk: p.diskPct,
    };
    gpus.forEach((g, idx) => {
      row[`gpu${g.index}_util`] = p.gpuUtilPcts[idx] ?? null;
      row[`gpu${g.index}_vram`] = p.gpuMemoryPcts[idx] ?? null;
      row[`gpu${g.index}_power`] = p.gpuPowerWs[idx] ?? null;
      row[`gpu${g.index}_temp`] = p.gpuTemperatureCs[idx] ?? null;
    });
    return row;
  });

  const tickFormatter = (iso: string) => {
    const d = new Date(iso);
    if (window === '1h' || window === '6h' || window === '24h') {
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' });
  };

  const formatPct = (v: unknown) => (typeof v === 'number' ? `${v.toFixed(1)} %` : String(v));
  const formatTemp = (v: unknown) => (typeof v === 'number' ? `${v.toFixed(1)} °C` : String(v));
  const formatPower = (v: unknown) => (typeof v === 'number' ? `${v.toFixed(1)} W` : String(v));

  return (
    <div className="bg-surface border border-border rounded-2xl p-4 space-y-4">
      <h3 className="text-sm font-semibold">시계열 추이</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {gpus.length > 0 ? (
          <>
            <ChartFrame title="GPU 사용률 (%)">
              <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
                <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
                <XAxis
                  dataKey="bucketAt"
                  tickFormatter={tickFormatter}
                  stroke={AXIS_COLOR}
                  tick={{ fontSize: 11 }}
                />
                <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainPct} unit="%" />
                <Tooltip
                  wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
                  formatter={formatPct}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {gpus.map((g, idx) => (
                  <Line
                    key={g.index}
                    type="monotone"
                    dataKey={`gpu${g.index}_util`}
                    name={`GPU ${g.index}`}
                    stroke={colorOf(idx)}
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ChartFrame>

            <ChartFrame title="GPU VRAM (%)">
              <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
                <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
                <XAxis
                  dataKey="bucketAt"
                  tickFormatter={tickFormatter}
                  stroke={AXIS_COLOR}
                  tick={{ fontSize: 11 }}
                />
                <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainPct} unit="%" />
                <Tooltip
                  wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
                  formatter={formatPct}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {gpus.map((g, idx) => (
                  <Line
                    key={g.index}
                    type="monotone"
                    dataKey={`gpu${g.index}_vram`}
                    name={`GPU ${g.index}`}
                    stroke={colorOf(idx)}
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ChartFrame>

            <ChartFrame title="GPU 전력량 (W)">
              <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
                <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
                <XAxis
                  dataKey="bucketAt"
                  tickFormatter={tickFormatter}
                  stroke={AXIS_COLOR}
                  tick={{ fontSize: 11 }}
                />
                <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainAuto} unit="W" />
                <Tooltip
                  wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
                  formatter={formatPower}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {gpus.map((g, idx) => (
                  <Line
                    key={g.index}
                    type="monotone"
                    dataKey={`gpu${g.index}_power`}
                    name={`GPU ${g.index}`}
                    stroke={colorOf(idx)}
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ChartFrame>

            <ChartFrame title="GPU 온도 (°C)">
              <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
                <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
                <XAxis
                  dataKey="bucketAt"
                  tickFormatter={tickFormatter}
                  stroke={AXIS_COLOR}
                  tick={{ fontSize: 11 }}
                />
                <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainAuto} unit="°C" />
                <Tooltip
                  wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
                  formatter={formatTemp}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {gpus.map((g, idx) => (
                  <Line
                    key={g.index}
                    type="monotone"
                    dataKey={`gpu${g.index}_temp`}
                    name={`GPU ${g.index}`}
                    stroke={colorOf(idx)}
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ChartFrame>
          </>
        ) : null}

        <ChartFrame title="CPU 사용률 (%)">
          <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
            <XAxis
              dataKey="bucketAt"
              tickFormatter={tickFormatter}
              stroke={AXIS_COLOR}
              tick={{ fontSize: 11 }}
            />
            <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainPct} unit="%" />
            <Tooltip
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              contentStyle={TOOLTIP_STYLE}
              labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
              formatter={formatPct}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="cpuUtil"
              name="CPU"
              stroke={colorOf(0)}
              dot={false}
              strokeWidth={1.5}
              connectNulls
            />
          </LineChart>
        </ChartFrame>

        <ChartFrame title="CPU 온도 (°C)">
          <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
            <XAxis
              dataKey="bucketAt"
              tickFormatter={tickFormatter}
              stroke={AXIS_COLOR}
              tick={{ fontSize: 11 }}
            />
            <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainAuto} unit="°C" />
            <Tooltip
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              contentStyle={TOOLTIP_STYLE}
              labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
              formatter={formatTemp}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="cpuTemp"
              name="CPU"
              stroke={colorOf(4)}
              dot={false}
              strokeWidth={1.5}
              connectNulls
            />
          </LineChart>
        </ChartFrame>

        <ChartFrame title="Memory (%)">
          <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
            <XAxis
              dataKey="bucketAt"
              tickFormatter={tickFormatter}
              stroke={AXIS_COLOR}
              tick={{ fontSize: 11 }}
            />
            <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainPct} unit="%" />
            <Tooltip
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              contentStyle={TOOLTIP_STYLE}
              labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
              formatter={formatPct}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="memory"
              name="Memory"
              stroke={colorOf(1)}
              dot={false}
              strokeWidth={1.5}
              connectNulls
            />
          </LineChart>
        </ChartFrame>

        <ChartFrame title="Disk (%)">
          <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
            <XAxis
              dataKey="bucketAt"
              tickFormatter={tickFormatter}
              stroke={AXIS_COLOR}
              tick={{ fontSize: 11 }}
            />
            <YAxis stroke={AXIS_COLOR} tick={{ fontSize: 11 }} domain={yDomainPct} unit="%" />
            <Tooltip
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              contentStyle={TOOLTIP_STYLE}
              labelFormatter={(iso) => new Date(String(iso)).toLocaleString('ko-KR')}
              formatter={formatPct}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="disk"
              name="Disk"
              stroke={colorOf(2)}
              dot={false}
              strokeWidth={1.5}
              connectNulls
            />
          </LineChart>
        </ChartFrame>
      </div>
    </div>
  );
}

function ChartFrame({ title, children }: { title: string; children: React.ReactElement }) {
  return (
    <div>
      <p className="text-xs text-foreground-muted mb-1">{title}</p>
      {/* recharts wrapper 의 클릭 focus outline 제거 — 차트 클릭 시 점선 박스 안 보이게. */}
      <div className="h-36 w-full [&_*:focus]:outline-none [&_*:focus-visible]:outline-none">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// 3. 분포 히스토그램
// ───────────────────────────────────────────────────────────────

/**
 * 분포 카드 — 통계 카드와 동일한 배치:
 *   1) GPU 통합 분포 카드 N장 (한 GPU 안에 사용률/VRAM/전력/온도 4 column).
 *   2) 시스템 분포 카드 4장 (CPU% / CPU°C / Mem / Disk).
 *
 * 서버측 bin 정의 (resource-metrics-range.ts):
 *   - % 메트릭: 0-20 / 20-40 / 40-60 / 60-80 / 80-100
 *   - 온도   : <40 / 40-55 / 55-70 / 70-80 / ≥80 (cpu_temp 와 gpu_temp 동일 척도)
 *   - 전력량 : <50 / 50-150 / 150-250 / 250-350 / ≥350
 */

interface MetricColumnDef {
  metric: ResourceMetricsRangeDistributionMetric;
  label: string;
  /** 헤더 옆에 표기할 단위. bin 라벨은 숫자만 노출하므로 헤더에서 단위를 알려줘야 의미 명확. */
  unit: string;
  /** 마지막 bin (upper === cap) 을 `≥{lower}` 표기로 바꾸기 위한 cap 값. */
  cap: number;
}

const GPU_DIST_COLUMNS: MetricColumnDef[] = [
  { metric: 'gpu_memory', label: 'VRAM', unit: '%', cap: 100 },
  { metric: 'gpu_util', label: '사용률', unit: '%', cap: 100 },
  { metric: 'gpu_power', label: '전력량', unit: 'W', cap: 9999 },
  { metric: 'gpu_temp', label: '온도', unit: '°C', cap: 200 },
];

const SYSTEM_DIST_CARDS: {
  metric: ResourceMetricsRangeDistributionMetric;
  title: string;
  unit: string;
  cap: number;
}[] = [
  { metric: 'cpu', title: 'CPU 사용률 분포', unit: '%', cap: 100 },
  { metric: 'cpu_temp', title: 'CPU 온도 분포', unit: '°C', cap: 200 },
  { metric: 'memory', title: 'Memory 사용률 분포', unit: '%', cap: 100 },
  { metric: 'disk', title: 'Disk 사용률 분포', unit: '%', cap: 100 },
];

/** bin 의 lower/upper → 사람 친화 라벨. 마지막 bin (upper === cap) 은 `≥{lower}`. */
function formatBinLabel(lower: number, upper: number, cap: number): string {
  if (upper === cap) return `≥${lower}`;
  return `${lower}-${upper}`;
}

function DistributionBlock({
  hostId,
  window,
}: {
  hostId: string;
  window: ResourceMetricsRangeWindow;
}) {
  // timeseries 응답에서 GPU 메타 (index, name) 를 가져와 GPU 통합 카드 N장 렌더.
  // 다른 블록과 캐시 공유라 추가 호출 비용 없음.
  const tsQuery = useQuery({
    queryKey: ['admin', 'resource-metrics-range-timeseries', hostId, window],
    queryFn: () => api.adminGetResourceMetricsRangeTimeseries(hostId, window),
    ...RANGE_QUERY_OPTIONS,
  });
  const gpus = tsQuery.data?.gpus ?? [];

  return (
    <div className="space-y-3">
      {gpus.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {gpus.map((g) => (
            <GpuDistributionCard key={`gpu-${g.index}`} hostId={hostId} window={window} gpu={g} />
          ))}
        </div>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {SYSTEM_DIST_CARDS.map((d) => (
          <SystemDistributionCard
            key={d.metric}
            hostId={hostId}
            window={window}
            metric={d.metric}
            title={d.title}
            unit={d.unit}
            cap={d.cap}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * GPU 통합 분포 카드 — 한 GPU 의 4 메트릭 분포를 4 column 으로.
 * 통계 카드와 동일 헤더/separator/column 스타일.
 */
function GpuDistributionCard({
  hostId,
  window,
  gpu,
}: {
  hostId: string;
  window: ResourceMetricsRangeWindow;
  gpu: { index: number; name: string };
}) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-3">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">GPU {gpu.index} 분포</h3>
          <p className="text-[10px] text-foreground-muted mt-0.5 truncate">{gpu.name}</p>
        </div>
      </header>
      <div className="grid grid-cols-4 divide-x divide-border">
        {GPU_DIST_COLUMNS.map((c) => (
          <div key={c.metric} className="flex flex-col gap-2 min-w-0 px-3 first:pl-0 last:pr-0">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider pb-1.5 border-b border-border">
              {c.label}{' '}
              <span className="text-foreground-muted font-normal normal-case">({c.unit})</span>
            </p>
            <DistributionBins
              hostId={hostId}
              window={window}
              metric={c.metric}
              gpuIndex={gpu.index}
              cap={c.cap}
              compact
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 시스템 분포 카드 — 한 메트릭의 분포만 표시. CPU%/CPU°C/Mem/Disk.
 * 통계 카드 (`SystemStatCard`) 와 동일한 카드 폭에 들어가도록 컴팩트 layout.
 */
function SystemDistributionCard({
  hostId,
  window,
  metric,
  title,
  unit,
  cap,
}: {
  hostId: string;
  window: ResourceMetricsRangeWindow;
  metric: ResourceMetricsRangeDistributionMetric;
  title: string;
  unit: string;
  cap: number;
}) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-2">
      <header>
        <h4 className="text-xs font-medium text-foreground-muted">
          {title} <span className="font-normal">({unit})</span>
        </h4>
      </header>
      <DistributionBins hostId={hostId} window={window} metric={metric} cap={cap} />
    </div>
  );
}

/**
 * bin 5개 리스트 — distribution API 호출 + 가로 bar 렌더. `compact` 면 GPU 카드 내 좁은
 * column 에 들어가도록 글자/간격 축소 + bucket count 생략.
 */
function DistributionBins({
  hostId,
  window,
  metric,
  gpuIndex,
  cap,
  compact = false,
}: {
  hostId: string;
  window: ResourceMetricsRangeWindow;
  metric: ResourceMetricsRangeDistributionMetric;
  gpuIndex?: number;
  cap: number;
  compact?: boolean;
}) {
  const distQuery = useQuery({
    queryKey: [
      'admin',
      'resource-metrics-range-distribution',
      hostId,
      window,
      metric,
      gpuIndex ?? null,
    ],
    queryFn: () => api.adminGetResourceMetricsRangeDistribution(hostId, window, metric, gpuIndex),
    ...RANGE_QUERY_OPTIONS,
  });

  if (distQuery.isLoading) {
    return <p className="text-xs text-foreground-muted">불러오는 중…</p>;
  }
  if (distQuery.error) {
    return (
      <p className="text-xs text-destructive">
        {distQuery.error instanceof ApiError ? distQuery.error.message : '오류'}
      </p>
    );
  }
  if (!distQuery.data || distQuery.data.totalBucketCount === 0) {
    return <p className="text-xs text-foreground-muted">데이터 없음</p>;
  }

  // compact (GPU 카드 내 4 column): 라벨 / bar / % 만. 좁은 공간 대응.
  // normal (시스템 카드): 라벨 / bar / % + (bucket count) 모두 노출 (기존 패턴).
  return (
    <ul className={compact ? 'space-y-1' : 'space-y-1'}>
      {distQuery.data.bins.map((b) => {
        const label = formatBinLabel(b.lower, b.upper, cap);
        const ratioPct = (b.ratio * 100).toFixed(1);
        return (
          <li
            key={`${b.lower}-${b.upper}`}
            className={cn(
              'grid items-center gap-2 text-[11px]',
              // compact: `150-250` 같은 3자리 from-to 까지 한 줄에 들어가도록 56px.
              // normal:  `(43K)` 같은 bucket count 까지 우측에 72px.
              compact ? 'grid-cols-[56px_1fr_38px]' : 'grid-cols-[56px_1fr_72px]',
            )}
          >
            <span className="text-foreground-muted tabular-nums whitespace-nowrap">{label}</span>
            <div className="h-2 bg-surface-muted rounded-full overflow-hidden">
              <div className="h-full bg-accent" style={{ width: `${b.ratio * 100}%` }} />
            </div>
            <span className="text-right tabular-nums whitespace-nowrap">
              {ratioPct}%
              {!compact ? (
                <span className="text-foreground-muted ml-1">
                  ({formatCountCompact(b.bucketCount)})
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ───────────────────────────────────────────────────────────────
// 4. 시간 × 요일 히트맵
// ───────────────────────────────────────────────────────────────

// 업무 패턴 기준 — 주중 (월~금) 을 위에, 주말 (토/일) 을 아래에 배치.
// 표시 순서 (dow=0~6 JS 표준: 0=일요일) 와 다르므로 별도 매핑 필요.
const DAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];
const DAY_ORDER_DOW = [1, 2, 3, 4, 5, 6, 0];
const HOURS = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
];

// 히트맵 select 옵션 — 분포와 동일한 메트릭 세트 + 사람 친화 라벨.
// GPU 메트릭은 gpuIndex 미지정으로 호출되어 모든 GPU 평균을 보여줌 ('평균' 배지).
const HEATMAP_METRIC_OPTIONS: {
  metric: ResourceMetricsRangeDistributionMetric;
  title: string;
}[] = [
  { metric: 'gpu_util', title: 'GPU 사용률' },
  { metric: 'gpu_memory', title: 'GPU VRAM' },
  { metric: 'gpu_power', title: 'GPU 전력량' },
  { metric: 'gpu_temp', title: 'GPU 온도' },
  { metric: 'cpu', title: 'CPU 사용률' },
  { metric: 'cpu_temp', title: 'CPU 온도' },
  { metric: 'memory', title: 'Memory 사용률' },
  { metric: 'disk', title: 'Disk 사용률' },
];

const GPU_METRICS = new Set<ResourceMetricsRangeDistributionMetric>([
  'gpu_util',
  'gpu_memory',
  'gpu_power',
  'gpu_temp',
]);

/** 히트맵 cell 라벨 단위 — `%` (사용률/VRAM), `°C` (온도), `W` (전력). */
function unitFor(metric: ResourceMetricsRangeDistributionMetric): string {
  if (metric === 'cpu_temp' || metric === 'gpu_temp') return ' °C';
  if (metric === 'gpu_power') return ' W';
  return ' %';
}

function HeatmapBlock({
  hostId,
  window,
}: {
  hostId: string;
  window: ResourceMetricsRangeWindow;
}) {
  const [metric, setMetric] = useState<ResourceMetricsRangeDistributionMetric>('gpu_util');

  // 1h/6h 윈도우는 격자 데이터가 빈약해 의미 약함 — 안내만.
  const meaningful = window !== '1h' && window !== '6h';

  // GPU 메트릭은 gpuIndex 미지정 → 서버가 모든 GPU 평균으로 처리.
  const isGpuMetric = GPU_METRICS.has(metric);

  const heatQuery = useQuery({
    queryKey: ['admin', 'resource-metrics-range-heatmap', hostId, window, metric, null],
    queryFn: () => api.adminGetResourceMetricsRangeHeatmap(hostId, window, metric, undefined),
    enabled: meaningful,
    ...RANGE_QUERY_OPTIONS,
  });

  // 셀 색상: 0~100 → 옅음 → 진함 그라데이션. accent hue 유지.
  const cellColor = (avg: number | null): string => {
    if (avg === null) return 'hsl(var(--surface-muted) / 0.4)';
    const clamped = Math.max(0, Math.min(100, avg));
    const opacity = 0.15 + clamped * 0.008; // 0.15 ~ 0.95
    return `hsl(var(--accent) / ${opacity.toFixed(2)})`;
  };

  const cellLabel = useMemo(() => {
    if (!heatQuery.data) return new Map<string, string>();
    const m = new Map<string, string>();
    const suffix = unitFor(metric);
    for (const c of heatQuery.data.cells) {
      const key = `${c.dayOfWeek}-${c.hourOfDay}`;
      m.set(key, c.avg !== null ? `${c.avg.toFixed(1)}${suffix}` : '—');
    }
    return m;
  }, [heatQuery.data, metric]);

  const cellAvg = useMemo(() => {
    if (!heatQuery.data) return new Map<string, number | null>();
    const m = new Map<string, number | null>();
    for (const c of heatQuery.data.cells) {
      m.set(`${c.dayOfWeek}-${c.hourOfDay}`, c.avg);
    }
    return m;
  }, [heatQuery.data]);

  return (
    <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">
          시간대 × 요일 히트맵
          {isGpuMetric ? (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-foreground-muted align-middle">
              평균
            </span>
          ) : null}
        </h3>
        <div className="relative">
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as ResourceMetricsRangeDistributionMetric)}
            aria-label="히트맵 메트릭 선택"
            className="h-8 appearance-none rounded-md border border-border bg-background pl-2 pr-7 text-xs"
          >
            {HEATMAP_METRIC_OPTIONS.map((opt) => (
              <option key={opt.metric} value={opt.metric}>
                {opt.title}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-3 -translate-y-1/2 text-foreground-muted" />
        </div>
      </div>

      {!meaningful ? (
        <p className="text-xs text-foreground-muted">
          7일 이상의 기간을 선택해야 의미 있는 패턴이 보입니다.
        </p>
      ) : heatQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : heatQuery.error ? (
        <p className="text-sm text-destructive">
          {heatQuery.error instanceof ApiError ? heatQuery.error.message : '알 수 없는 오류'}
        </p>
      ) : (
        // pt-7: 첫 row(월요일) 셀 위로 띄우는 tooltip 이 잘리지 않도록 상단 여유 확보.
        <div className="overflow-x-auto pt-7">
          <table className="w-full text-[10px] tabular-nums">
            <thead>
              <tr>
                <th className="w-8" />
                {HOURS.map((h) => (
                  <th
                    key={`hour-${h}`}
                    className="text-foreground-muted font-normal pb-1"
                    style={{ minWidth: 18 }}
                  >
                    {h % 3 === 0 ? h : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAY_LABELS.map((dayLabel, displayIdx) => {
                const dow = DAY_ORDER_DOW[displayIdx] ?? 0;
                return (
                  <tr key={`row-${dayLabel}`}>
                    <td className="text-foreground-muted pr-2 text-right">{dayLabel}</td>
                    {HOURS.map((h) => {
                      const key = `${dow}-${h}`;
                      const avg = cellAvg.get(key) ?? null;
                      return (
                        <td
                          key={`cell-${key}`}
                          className="relative h-5 border border-border group"
                          style={{ backgroundColor: cellColor(avg) }}
                        >
                          <span className="pointer-events-none absolute left-1/2 bottom-full -translate-x-1/2 mb-1 px-2 py-1 rounded-md border border-border bg-surface text-foreground text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-100 z-20 shadow-lg">
                            {dayLabel} {String(h).padStart(2, '0')}시 · {cellLabel.get(key)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[10px] text-foreground-muted mt-2">
            셀에 마우스 올리면 평균값이 표시됩니다. 색이 진할수록 사용률이 높음.
          </p>
        </div>
      )}
    </div>
  );
}
